import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, resolveComponent as _resolveComponent, createBlock as _createBlock } from "vue"

const _hoisted_1 = { id: "modal-container-element" }
const _hoisted_2 = { class: "description" }
const _hoisted_3 = ["actions"]
const _hoisted_4 = { key: 1 }
const _hoisted_5 = ["rows", "total-items", "current-page", "loading"]

import { useStore } from 'vuex';
import { computed, ref, onMounted, watchEffect, watch } from 'vue';
import { debounce } from 'lodash';

import EpinioCatalogServiceModel from '../models/catalogservices';
import { EPINIO_TYPES } from '../types';
import { makeStateTag } from '../utils/table-formatters';
import ServiceDeleteModal from '../components/service/ServiceDeleteModal.vue';
import ServiceInstanceModal from '../components/service/ServiceInstanceModal.vue';
import { makeActionMenu } from '../utils/table-formatters';
import Masthead from '@shell/components/ResourceList/Masthead';
import CatalogServiceModal from '../components/service/CatalogServiceModal.vue';
import CatalogServiceDeleteModal from '../components/service/CatalogServiceDeleteModal.vue';
import { ResourceQueryOptions } from '../models/resource/ui-types';
import { useServices } from '../queries/useServiceQueries';
import { CatalogService } from '../models/catalogservice/ui-types';
import { ServiceInstance, ListServiceInstancesRequestParams } from '../models/service/ui-types';
import { ResourceTableRow } from '../models/resource/ui-types';
import { makeNameLinks } from '../utils/table-formatters';
import { useCatalogService } from '../queries/useCatalogServicesQueries';


export default /*@__PURE__*/_defineComponent({
  __name: 'catalogservices',
  props: {
    value: {}
  },
  setup(__props: any) {

const store = useStore();
const router = store.$router;
const t = store.getters['i18n/t'];

const props = __props;

const resource: string = EPINIO_TYPES.CATALOG_SERVICE;
const serviceDeleteModal = ref<InstanceType<typeof ServiceDeleteModal> | null>(null);
const serviceModal = ref<InstanceType<typeof ServiceInstanceModal> | null>(null);
const catalogServiceModal = ref<InstanceType<typeof CatalogServiceModal> | null>(null);
const catalogServiceDeleteModal = ref<InstanceType<typeof CatalogServiceDeleteModal> | null>(null);
const tableEl = ref<any>(null);

const requestParams = ref<ListServiceInstancesRequestParams>({
  page: 1,
  pageSize: 10,
  search: '',
  namespaces: undefined,
  catalogService: props.value.meta.name,
});

const requestOptions = ref<ResourceQueryOptions>({
  enabled: true,
  polling: true,
  isTablePagination: true,
});

const searchQuery = ref<string>('');

watch(searchQuery, (newQuery) => {
  onSearch(newQuery);
});

const onSearch = debounce(async (query: string) => {
  requestParams.value.page = 1;
  requestParams.value.search = query;
}, 500);

const {data: catalogService, isError: isErrorCatalogService, error: catalogServiceError} = useCatalogService(store, ref(props.value.meta.name));
const {data: services, isLoading: isLoadingServices, isError: isErrorServices, error: servicesError} = useServices(store, requestParams, requestOptions);

const canEditService = computed(() => {
  const can = store.getters['epinio/can'];

  return can && (can('service_write') || can('service'));
});
const canDeleteService = canEditService;
const canCreateService = canEditService;

const canEditCatalogService = computed(() => {
  const can = store.getters['epinio/can'];

  return can && (can('catalog_service_write') || can('catalog_service'));
});
const canDeleteCatalogService = canEditCatalogService;

const availableActions = computed(() => {
  if (!catalogService.value) {
    return [];
  } 

  const rowActions = (row: CatalogService) => {
    const out: any[] = [];

    if (canCreateService.value) {
      out.push({
        label: t('epinio.services.create.label'),
        enabled: true,
        action: () => serviceModal.value?.openCreate(row.meta.name)
      });
    }
    if (canEditCatalogService.value) {
      out.push({
        label: t('generic.edit'),
        enabled: true,
        action: () => catalogServiceModal.value?.openEdit(row),

      });
    }
    if (canDeleteCatalogService.value) {
      out.push({
        enabled: true,
        label: t('generic.delete'),
        action: () => catalogServiceDeleteModal.value?.openDelete(row),
      });
    }

    return out;
  };

  return rowActions(catalogService.value);
});

const displayRows = computed(() => {
  if (!services.value) {
    return [];
  }
  
  const rows: ResourceTableRow<ServiceInstance>[] = (services.value.items ?? []).map((s) => ({
    ...s,
    id: s.meta.name, // stable, unique per namespace
    availableActions: [{
      label: 'Delete',
      action: () => openServiceDeleteModal(s),
      enabled: canDeleteService.value,
      visible: canDeleteService.value,
      danger: true,
    }, {
      label: 'Edit',
      action: () => openServiceEditModal(s),
      enabled: canEditService.value,
      visible: canEditService.value,
    }],
    canDelete: canDeleteService.value,
  }));
  return rows;
});

// Watch for changes to the active namespace cache and update the request params accordingly
watchEffect(() => {
  void store.state.activeNamespaceCacheKey;
  const activeNamespaces = store.state.activeNamespaceCache;

  if (activeNamespaces && Object.keys(activeNamespaces).length > 0) {
    requestParams.value.namespaces = Object.keys(activeNamespaces);
  } else {
    requestParams.value.namespaces = undefined;
  }
});

const openServiceDeleteModal = (service: ServiceInstance) => {
  serviceDeleteModal.value?.openDelete(service);
};

const openServiceEditModal = (service: ServiceInstance) => {
  serviceModal.value?.openEdit(service);
};

onMounted(() => {
  store.dispatch('epinio/me');
});

// Services without service_write/service permission on that row can't be
// individually deleted, so they're excluded from bulk selection too.
const isRowSelectable = (row: any) => row.canDelete;

// NOTE: must be a named function declared here, not an inline arrow function
// in the template, see feedback_vue_ref_unwrap_bug.md. Vue auto-unwraps
// top-level refs inside template expressions, so referencing `tableEl`
// directly in an inline template callback gives the already-unwrapped
// (initially null) value instead of the Ref object, and `tableEl.value = el`
// throws, silently aborting the rest of the block.
const setTableRef = (el: any) => {
  if (el) {
    tableEl.value = el;
    el.renderActions = makeActionMenu;
    el.rowSelectable = isRowSelectable;
  }
};

const handleNavigate = (event: CustomEvent) => {
  router.push(event.detail.url);
};

const columns = [
  {
    field: 'stateDisplay',
    label: 'State',
    width: '100px',
    formatter: (_v: any, row: ServiceInstance) => makeStateTag(row)
  },
  {
    field: 'nameDisplay',
    label: 'Name',
    formatter: (_v: any, row: ServiceInstance) => {
      const el = document.createElement('a');

      el.textContent = row.meta?.name || '';
      el.style.cursor = 'pointer';
      el.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        serviceModal.value?.openView(row);
      });

      return el;
    }
  },
  {
    field: 'meta.namespace',
    label: 'Namespace'
  },
  {
    field: 'catalogService',
    label: 'Catalog Service',
    sortable: false,
    formatter: (_v: any, row: ServiceInstance) => makeNameLinks(
      [row.catalogService],
      { cluster: store.getters['clusterId'], resource: EPINIO_TYPES.CATALOG_SERVICE },
      router
    )
  },
  {
    field: 'catalogServiceVersion',
    label: 'Catalog Service Version'
  },
  {
    field: 'boundApps',
    label: 'Bound Applications',
    sortable: false,
    formatter: (_v: any, row: ServiceInstance) => makeNameLinks(
      row.boundApps,
      { cluster: store.getters['clusterId'], namespace: row.meta?.namespace, resource: EPINIO_TYPES.APP },
      router
    )
  },
  {
    field: 'meta.createdAt',
    label: 'Age',
    formatter: 'age'
  }
];

function handleDeleted() {
  store.$router.push({
    name: 'epinio-c-cluster-resource',
    params: {
      ...store.$router.currentRoute.params,
      resource: 'catalogservices',
    },
  });
}

return (_ctx: any,_cache: any) => {
  const _component_Banner = _resolveComponent("Banner")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_unref(Masthead), {
      schema: _ctx.value,
      resource: _unref(resource),
      "type-display": _unref(t)('epinio.catalogService.detail.servicesTitle', { catalogService: _unref(catalogService)?.meta.name })
    }, {
      subHeader: _withCtx(() => [
        _createElementVNode("p", _hoisted_2, _toDisplayString(_unref(catalogService)?.description ?? ''), 1)
      ]),
      createButton: _withCtx(() => [
        (availableActions.value.length > 0 && canEditCatalogService.value)
          ? (_openBlock(), _createElementBlock("trailhand-action-menu", {
              key: 0,
              actions: availableActions.value
            }, null, 8, _hoisted_3))
          : (_openBlock(), _createElementBlock("div", _hoisted_4))
      ]),
      _: 1,
      __: [1]
    }, 8, ["schema", "resource", "type-display"]),
    _cache[2] || (_cache[2] = _createTextVNode()),
    (_unref(isErrorServices) || _unref(isErrorCatalogService))
      ? (_openBlock(), _createBlock(_component_Banner, {
          key: 0,
          color: "error",
          label: _unref(servicesError)?.message || _unref(catalogServiceError)?.message || _unref(t)('epinio.service.errors.fetchAll')
        }, null, 8, ["label"]))
      : _createCommentVNode("", true),
    _cache[3] || (_cache[3] = _createTextVNode()),
    _createElementVNode("trailhand-table", {
      ref: setTableRef,
      rows: displayRows.value,
      columns: columns,
      searchable: false,
      "server-side": true,
      "total-items": _unref(services)?.totalItems ?? 0,
      "current-page": requestParams.value.page,
      loading: _unref(isLoadingServices),
      "key-field": "id",
      onNavigate: handleNavigate,
      onPageChange: _cache[0] || (_cache[0] = (e) => { requestParams.value.page = e.detail.page; })
    }, null, 40, _hoisted_5),
    _cache[4] || (_cache[4] = _createTextVNode()),
    _createVNode(ServiceDeleteModal, {
      ref_key: "serviceDeleteModal",
      ref: serviceDeleteModal
    }, null, 512),
    _cache[5] || (_cache[5] = _createTextVNode()),
    _createVNode(ServiceInstanceModal, {
      ref_key: "serviceModal",
      ref: serviceModal
    }, null, 512),
    _cache[6] || (_cache[6] = _createTextVNode()),
    _createVNode(CatalogServiceModal, {
      ref_key: "catalogServiceModal",
      ref: catalogServiceModal
    }, null, 512),
    _cache[7] || (_cache[7] = _createTextVNode()),
    _createVNode(CatalogServiceDeleteModal, {
      ref_key: "catalogServiceDeleteModal",
      ref: catalogServiceDeleteModal,
      onDeleted: handleDeleted
    }, null, 512)
  ]))
}
}

})